// Shared, pure data-shape helpers.
//
// These power two SPA features and MUST stay correct because they sit on
// the data path between an operator's input and a rendered PDF:
//   - AcroForm auto-binding on template upload (field name → JSON key,
//     inline schema)
//   - CSV bulk-job import (dot-notation headers → nested JSON) and the
//     template-aware sample body the bulk-job modal pre-fills
//
// They live here (not inline in public/static/spa.js) so the same source
// is BOTH bundled into the SPA (esbuild, bundle:true) AND unit-tested
// under node (__test__/unit/data-shape.test.js). No DOM/React deps — keep
// it that way so the module stays node-loadable.

// Normalize ONE path segment of an AcroForm field name into a valid,
// predictable JSON key. AcroForm names are free text ("First Name",
// "Betrag (€)", "1_total") so we:
//   - NFKD-decompose and drop combining accents (ä → a)
//   - replace every run of non [A-Za-z0-9_] with a single underscore
//   - trim leading/trailing underscores
//   - guarantee it doesn't start with a digit (so the key is also a safe
//     identifier for dot-notation / CSV headers / template variables)
//   - never return empty
function normalizeJsonKey(seg) {
  let k = String(seg == null ? '' : seg).normalize('NFKD').replace(/[̀-ͯ]/g, '');
  k = k.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  if (!k) k = 'field';
  if (/^[0-9]/.test(k)) k = '_' + k;
  return k;
}

// AcroForm fully-qualified names use '.' to denote hierarchy
// ("address.city"). Preserve that: normalize each segment and rejoin with
// '.', so the resulting jsonPath nests correctly through the dot-path
// resolver (server + renderer) and lines up with CSV dot-notation.
function acroNameToPath(name) {
  return String(name == null ? '' : name)
    .split('.')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map(normalizeJsonKey)
    .join('.') || 'field';
}

// Write `value` into `obj` at a dot-separated path, creating intermediate
// objects as needed (auto-vivify). Inverse of flattenDotKeys.
function setPathDeep(obj, path, value) {
  const parts = String(path).split('.').map((p) => p.trim()).filter(Boolean);
  if (!parts.length) return;
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (typeof cur[p] !== 'object' || cur[p] === null) cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

// Build an INLINE JSON Schema (object with nested properties) from the
// detected/placed fields, keyed by each field's jsonPath. checkbox →
// boolean, everything else → string. Radio rows share a jsonPath (the
// group), so they collapse to one leaf. Returns null when no field
// carries a jsonPath (nothing to describe).
function buildInlineSchemaFromFields(fields) {
  if (!Array.isArray(fields)) return null;
  const root = { type: 'object', properties: {} };
  let any = false;
  for (const f of fields) {
    const path = (f && typeof f.jsonPath === 'string') ? f.jsonPath.trim() : '';
    if (!path) continue;
    const parts = path.split('.').map((p) => p.trim()).filter(Boolean);
    if (!parts.length) continue;
    const leafType = f.type === 'checkbox' ? 'boolean' : 'string';
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!node.properties[p] || node.properties[p].type !== 'object') {
        node.properties[p] = { type: 'object', properties: {} };
      }
      node = node.properties[p];
    }
    const leaf = parts[parts.length - 1];
    // Don't clobber an object node we already created for a deeper path.
    if (!node.properties[leaf] || node.properties[leaf].type !== 'object') {
      node.properties[leaf] = { type: leafType };
    }
    any = true;
  }
  return any ? root : null;
}

// Minimal, dependency-free CSV reader. Handles quoted fields, embedded
// commas, escaped quotes (""), embedded newlines inside quotes, BOM, and
// CRLF/LF line endings. Returns an array of string-cell arrays.
function parseCsvRows(text) {
  const rows = [];
  let row = [], field = '', i = 0, inQuotes = false;
  const s = String(text).replace(/^﻿/, '');
  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  // Drop blank lines (a single empty cell) — common from a trailing newline.
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

// Flatten a nested object into dot-notation { "a.b.c": value } pairs —
// the inverse of setPathDeep. Arrays and leaf scalars are emitted as-is
// (CSV can't express arrays, so a sample array just stringifies). Used to
// turn a JSON sample body into CSV headers + a value row.
function flattenDotKeys(obj, prefix, out) {
  out = out || {};
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    const keys = Object.keys(obj);
    if (keys.length === 0 && prefix) { out[prefix] = ''; return out; }
    for (const k of keys) flattenDotKeys(obj[k], prefix ? `${prefix}.${k}` : k, out);
  } else if (prefix) {
    out[prefix] = obj;
  }
  return out;
}

// Quote a single CSV cell when it contains a comma, quote, or newline;
// embedded quotes are doubled per RFC 4180. Mirrors what parseCsvRows
// reads back.
function csvEscapeCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Convert CSV text into the bulk-job item shape [{ data, filename? }].
// The header row carries dot-notation column names; each is expanded into
// a nested path under the item's `data`. A column named exactly
// `filename` is lifted out as the per-item output filename (matches the
// job worker's `item.filename`). Cells stay strings — the renderer
// stringifies anyway, and coercing "007" → 7 would corrupt data.
function csvToJobItems(text) {
  const rows = parseCsvRows(text);
  if (!rows.length) throw new Error('CSV is empty');
  const headers = rows[0].map((h) => h.trim());
  if (!headers.length || headers.every((h) => h === '')) throw new Error('CSV has no header row');
  const items = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    const data = {};
    let filename = null;
    for (let c = 0; c < headers.length; c++) {
      const key = headers[c];
      if (!key) continue;
      const val = cells[c] !== undefined ? cells[c] : '';
      if (key === 'filename') { filename = val || null; continue; }
      setPathDeep(data, key, val);
    }
    const item = { data };
    if (filename) item.filename = filename;
    items.push(item);
  }
  return items;
}

// Build a `data: { ... }` sample request body from a JSON Schema. Walks
// `properties` and emits placeholder values typed by the leaf schema's
// `type`. Mirrors the editor's "Sample request body" preview.
function sampleFromSchema(schema) {
  const sampleValue = (s) => {
    if (!s || typeof s !== 'object') return '<value>';
    const t = Array.isArray(s.type) ? s.type[0] : s.type;
    if (s.enum && s.enum.length > 0) return s.enum[0];
    if (t === 'object' && s.properties) {
      const out = {};
      for (const [k, sub] of Object.entries(s.properties)) out[k] = sampleValue(sub);
      return out;
    }
    if (t === 'array') return [sampleValue(s.items || {})];
    if (t === 'integer') return 0;
    if (t === 'number') return 0;
    if (t === 'boolean') return false;
    if (t === 'null') return null;
    return `<${s.description || s.title || t || 'value'}>`;
  };
  return { data: sampleValue(schema || {}) };
}

// Static fallback samples — used when a template carries no schema/fields
// to describe.
const JSON_SAMPLE = '[\n  { "data": { "name": "Alice" } },\n  { "data": { "name": "Bob" } }\n]';
const CSV_SAMPLE = 'name,address.city,filename\nAlice,Berlin,alice.pdf\nBob,Hamburg,bob.pdf';

// Resolve a template's effective schema for sample generation: inline
// jsonSchema first, then a linked saved schema (looked up in `savedById`),
// then one derived from the field jsonPaths, else null. Same precedence
// the server uses, with the field-derived fallback added.
function schemaForTemplate(tpl, savedById) {
  if (!tpl) return null;
  if (tpl.jsonSchema && typeof tpl.jsonSchema === 'object') return tpl.jsonSchema;
  if (tpl.schemaId && savedById && savedById[tpl.schemaId]) return savedById[tpl.schemaId];
  return buildInlineSchemaFromFields(tpl.fields);
}

// Produce the bulk-job textarea sample for a template, in the requested
// format. JSON → an array with one { data } item; CSV → dot-notation
// headers + one placeholder value row. Falls back to the static samples
// when the template has no schema/fields.
function jobSampleText(fmt, schema) {
  const hasProps = schema && schema.properties && Object.keys(schema.properties).length > 0;
  if (!hasProps) return fmt === 'csv' ? CSV_SAMPLE : JSON_SAMPLE;
  const sample = sampleFromSchema(schema); // { data: {...} }
  if (fmt === 'csv') {
    const flat = flattenDotKeys(sample.data || {}, '');
    const headers = Object.keys(flat);
    if (!headers.length) return CSV_SAMPLE;
    const headerRow = headers.map(csvEscapeCell).join(',');
    const valueRow = headers.map((h) => csvEscapeCell(flat[h])).join(',');
    return `${headerRow}\n${valueRow}`;
  }
  return JSON.stringify([sample], null, 2);
}

// ---------------------------------------------------------------------------
// Fill-in form ("Fill out" in the editor, "Form" mode when generating).
//
// Non-developers fill a template through a generated form instead of
// typing a JSON body. These helpers turn a template's placed fields into
// form inputs and the entered values back into the `data` object the
// /generate endpoint expects — following the renderer's value contract:
//   - a value is read from data at the field's dot-separated jsonPath;
//     when the path is MISSING the field's `default` is used, but an
//     empty string is a real value and wins over the default — so blank
//     inputs are omitted rather than sent as "".
//   - checkbox: truthy strings/booleans ("true", "1", "yes", "x", …)
//   - radio: the selected widget's radioIndex (or its option label);
//     every widget of a group shares the group's jsonPath.
//   - image: a URL or a data: URI.
// Values are coerced to the leaf type of the template's effective schema
// because server-side validation (validateData) runs ajv with
// coerceTypes:false.
// ---------------------------------------------------------------------------

// Field name → data key for fields the user hasn't bound by hand:
// "Customer name" → "customer_name". Same character rules as
// normalizeJsonKey, lower-cased so keys read like typical JSON.
function fieldKeyFromName(name) {
  return normalizeJsonKey(String(name == null ? '' : name).toLowerCase());
}

// Next "<base> N" that no field uses yet (by name or by its derived data
// key). Counting fields (`length + 1`) reused a deleted field's number,
// so a new field silently shared a data key with a surviving one.
function nextFieldName(fields, base) {
  const list = Array.isArray(fields) ? fields : [];
  const names = new Set(list.map((f) => f && f.name));
  const keys = new Set(list.map((f) => f && f.jsonPath).filter(Boolean));
  for (let n = list.length + 1; ; n++) {
    const name = `${base} ${n}`;
    if (!names.has(name) && !keys.has(fieldKeyFromName(name))) return name;
  }
}

// `key`, or key_2, key_3 … — unique among the OTHER fields' data keys.
// Used for keys derived automatically from names, never for keys a
// person typed (sharing a key on purpose prints one value twice).
function uniqueDataKey(fields, key, selfId) {
  const used = new Set((Array.isArray(fields) ? fields : [])
    .filter((f) => f && f.id !== selfId)
    .map((f) => f.jsonPath)
    .filter(Boolean));
  if (!used.has(key)) return key;
  for (let i = 2; ; i++) {
    if (!used.has(`${key}_${i}`)) return `${key}_${i}`;
  }
}

// "customer_name" / "customerName" → "Customer name". Used as the form
// label when a field has no human name of its own.
function humanizeKey(key) {
  const last = String(key == null ? '' : key).split('.').pop() || '';
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : String(key || '');
}

// Form label for a field. Names typed by a person ("Customer name") are
// used as-is; machine-style names — AcroForm ids like
// "transaction.paymentId", "first_name" or a bare lowercase "vorname" —
// are turned into words.
function fillLabel(f, path) {
  const raw = String((f && (f.name || f.formFieldName)) || '').trim();
  if (!raw) return humanizeKey(path);
  const keyLike = /^[A-Za-z0-9_.\-]+$/.test(raw)
    && (/[._\-]/.test(raw) || /[a-z][A-Z]/.test(raw) || /^[a-z][a-z0-9]*$/.test(raw));
  return keyLike ? humanizeKey(raw) : raw;
}

// The JSON Schema node at a dot path, or null.
function schemaNodeAt(schema, path) {
  let node = schema;
  for (const part of String(path).split('.').filter(Boolean)) {
    if (!node || typeof node !== 'object' || !node.properties || typeof node.properties !== 'object') return null;
    node = node.properties[part];
  }
  return node && typeof node === 'object' ? node : null;
}

// Primary JSON type ("string", "number", "integer", "boolean", …) of the
// schema leaf at `path`, or null when the schema doesn't describe it.
function schemaLeafType(schema, path) {
  const node = schemaNodeAt(schema, path);
  if (!node) return null;
  const t = Array.isArray(node.type) ? node.type.find((x) => x !== 'null') : node.type;
  return typeof t === 'string' ? t : null;
}

function isTruthyValue(v) {
  if (v === true) return true;
  if (v === false || v == null) return false;
  return ['true', '1', 'yes', 'on', 'x', 'checked'].includes(String(v).trim().toLowerCase());
}

const DATE_FORMATS = new Set(['date-dmy', 'date-ymd']);
// Field types whose value comes from data. Shapes are decoration and
// signature fields are filled by the signing flow, never by data.
const DATA_FIELD_TYPES = new Set(['text', 'multiline', 'number', 'currency', 'checkbox', 'radio', 'image', 'barcode']);

// Build the form model: one input per distinct jsonPath, in reading
// order (page, then top-to-bottom, then left-to-right). Returns
//   [{ path, kind, label, required, defaultValue, options?, currencyCode? }]
// kind ∈ text | multiline | number | currency | date | checkbox | radio | image | barcode.
// defaultValue is a string for every kind except checkbox (boolean).
function fillFormFromFields(fields, schema) {
  if (!Array.isArray(fields)) return [];
  const ordered = fields
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => f && typeof f.jsonPath === 'string' && f.jsonPath.trim()
      && DATA_FIELD_TYPES.has(f.type || 'text'))
    .sort((a, b) => ((a.f.page || 1) - (b.f.page || 1))
      || ((Number(a.f.y) || 0) - (Number(b.f.y) || 0))
      || ((Number(a.f.x) || 0) - (Number(b.f.x) || 0))
      || (a.i - b.i));

  const byPath = new Map();
  for (const { f } of ordered) {
    const path = f.jsonPath.trim();
    const type = f.type || 'text';
    let entry = byPath.get(path);
    if (!entry) {
      let kind = type;
      if ((type === 'text' || type === 'multiline') && DATE_FORMATS.has(f.format)) kind = 'date';
      const node = schemaNodeAt(schema, path);
      if (kind === 'text' && node && (node.format === 'date' || node.format === 'date-time')) kind = 'date';
      entry = {
        path,
        kind,
        label: fillLabel(f, path),
        required: !!f.required,
        // Whitespace-only defaults (AcroForm widgets often carry " ")
        // count as empty: otherwise the input starts with a stray space
        // that ends up in front of whatever the user types.
        defaultValue: kind === 'checkbox' ? isTruthyValue(f.default)
          : (f.default == null || String(f.default).trim() === '' ? '' : String(f.default)),
      };
      if (kind === 'currency' && f.currencyCode) entry.currencyCode = String(f.currencyCode).toUpperCase();
      if (node && Array.isArray(node.enum) && node.enum.length && kind === 'text') {
        entry.kind = 'select';
        entry.options = node.enum.map((v) => ({ value: String(v), label: String(v) }));
      }
      byPath.set(path, entry);
    } else if (f.required) {
      entry.required = true;
    }
    if (entry.kind === 'radio') {
      // AcroForm radios carry the whole group in radioOptions; manually
      // built groups don't, so fall back to one option per widget.
      const opts = entry.options || [];
      const add = (index, label) => {
        const value = String(index);
        if (!opts.some((o) => o.value === value)) {
          opts.push({ value, label: (label && String(label).trim()) || `Option ${Number(index) + 1}` });
        }
      };
      if (Array.isArray(f.radioOptions) && f.radioOptions.length) {
        for (const o of f.radioOptions) if (o && o.index != null) add(o.index, o.label);
      } else if (f.radioIndex != null) {
        add(f.radioIndex, f.name);
      }
      opts.sort((a, b) => Number(a.value) - Number(b.value));
      entry.options = opts;
      // A label default ("Yes") becomes the matching option's index.
      if (entry.defaultValue && !opts.some((o) => o.value === entry.defaultValue)) {
        const hit = opts.find((o) => o.label === entry.defaultValue);
        entry.defaultValue = hit ? hit.value : '';
      }
      // Radio groups are labelled by the group, not by one widget.
      if (f.formFieldName) entry.label = fillLabel({ name: f.formFieldName }, path);
    }
  }
  return [...byPath.values()];
}

// Initial form values (path → value) seeded from field defaults.
function initialFillValues(formFields) {
  const out = {};
  for (const ff of formFields || []) out[ff.path] = ff.defaultValue;
  return out;
}

// Coerce one entered value to what the schema (or, without a schema,
// the renderer) expects. Returns undefined to mean "omit this key".
function coerceFillValue(ff, raw, schema) {
  const leaf = schemaLeafType(schema, ff.path);
  if (ff.kind === 'checkbox') {
    const on = isTruthyValue(raw);
    if (leaf === 'string') return on ? 'true' : 'false';
    return on;
  }
  const s = raw == null ? '' : (typeof raw === 'string' ? raw : String(raw));
  if (s.trim() === '') {
    // Blank: omit it so the renderer uses the field default — unless the
    // field HAS a default and the user cleared it (the form starts
    // pre-filled with defaults). Then send "" so nothing is printed;
    // the renderer treats "" as a real value. Not for typed schema leaves,
    // where "" would fail validation.
    const hasDefault = ff.defaultValue != null && String(ff.defaultValue) !== '';
    return hasDefault && (!leaf || leaf === 'string') ? '' : undefined;
  }
  if (leaf === 'number' || leaf === 'integer') {
    const n = Number(s.replace(',', '.'));
    if (Number.isFinite(n)) return leaf === 'integer' ? Math.trunc(n) : n;
    return s; // let validation report it rather than silently dropping
  }
  if (leaf === 'boolean') return isTruthyValue(s);
  return s;
}

// Entered values → the `data` object for /generate.
function buildFillData(formFields, values, schema) {
  const data = {};
  for (const ff of formFields || []) {
    const v = coerceFillValue(ff, values ? values[ff.path] : undefined, schema);
    if (v !== undefined) setPathDeep(data, ff.path, v);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Live-preview formatting: what the renderer prints for a field value.
// Mirrors render-service (format.go applyFormat, renderer.go formatNumber /
// formatCurrencyField / currencySymbol) so the fill view shows the same
// text the PDF will. `defaults` is the tenant's template defaults:
// { numberFormat, currencyFormat, defaultCurrency }.
// ---------------------------------------------------------------------------

// toFixed with Go's FormatFloat rounding: an exact tie goes to the even
// digit (0.125 → "0.12"); JS toFixed would round it up.
function toFixedHalfEven(n, places) {
  const s = n.toFixed(places);
  const scaled = Math.abs(n) * Math.pow(10, places);
  if (scaled - Math.floor(scaled) === 0.5 && Math.floor(scaled) % 2 === 0) {
    const down = (Math.sign(n) * Math.floor(scaled)) / Math.pow(10, places);
    return down.toFixed(places);
  }
  return s;
}

// "1234.56" (dot decimal, ungrouped) → the chosen separator style.
function applyNumberSeparators(raw, format) {
  const eu = format === 'eu-grouped' || format === 'eu-plain';
  const grouped = format !== 'us-plain' && format !== 'eu-plain';
  let [intPart, frac = ''] = String(raw).split('.');
  const neg = intPart.startsWith('-');
  if (neg) intPart = intPart.slice(1);
  if (grouped && intPart.length > 3) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, eu ? '.' : ',');
  }
  return (neg ? '-' : '') + intPart + (frac ? (eu ? ',' : '.') + frac : '');
}

// Number / currency fields in the fill-out are typed straight onto the
// page, into a plain text box (it shows the formatted amount when not
// focused). Keep only what a number can contain — digits, "." and ",",
// and a leading minus — so letters can't be typed, as in the panel's
// number input.
function numberInputChars(v) {
  const s = String(v == null ? '' : v);
  return (/^\s*-/.test(s) ? '-' : '') + s.replace(/[^\d.,]/g, '');
}

// A typed amount → a plain number string ("1234.5"), the shape the panel's
// number input produces and the renderer parses. With both separators the
// last one is the decimal separator ("1.234,5", "1,234.5"). A lone one is
// decimal when it's the format's decimal symbol (decimalComma: EU number
// formats) or not followed by exactly three digits; otherwise it groups
// thousands ("1,234" in a US format, "1.234" in an EU one). Anything that
// isn't a number is returned as typed.
function normalizeNumberInput(v, decimalComma) {
  const s = String(v == null ? '' : v).trim();
  const m = /^(-?)(\d*(?:[.,]\d*)*)$/.exec(s);
  if (!m || !/\d/.test(m[2])) return s;
  const sign = m[1];
  const body = m[2];
  const seps = body.replace(/\d/g, '');
  let decimalAt = -1;
  if (seps.includes('.') && seps.includes(',')) {
    decimalAt = Math.max(body.lastIndexOf('.'), body.lastIndexOf(','));
  } else if (seps.length === 1) {
    const at = body.indexOf(seps);
    const groupsThousands = /^\d{1,3}$/.test(body.slice(0, at)) && /^\d{3}$/.test(body.slice(at + 1));
    if (seps === (decimalComma ? ',' : '.') || !groupsThousands) decimalAt = at;
  } else if (seps.length > 1) {
    // One separator, several times: thousands groups ("1.234.567") — only
    // when every group after the first has three digits.
    const groups = body.split(seps[0]);
    if (!/^\d{1,3}$/.test(groups[0]) || !groups.slice(1).every((g) => /^\d{3}$/.test(g))) return s;
  }
  if (decimalAt < 0) return sign + body.replace(/[.,]/g, '');
  const frac = body.slice(decimalAt + 1);
  if (/[.,]/.test(frac)) return s;
  return sign + body.slice(0, decimalAt).replace(/[.,]/g, '') + '.' + frac;
}

function parseNumberLoose(v) {
  const s = String(v == null ? '' : v).trim();
  if (s === '' || !/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const CURRENCY_SYMBOLS = {
  USD: '$', AUD: '$', CAD: '$', NZD: '$', SGD: '$', HKD: '$', MXN: '$', ARS: '$',
  EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', TRY: '₺', INR: '₹', KRW: '₩', RUB: '₽',
  BRL: 'R$', ZAR: 'R', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CHF: 'CHF ',
};
function currencySymbol(code) {
  return CURRENCY_SYMBOLS[code] || code + ' ';
}

// Accepts what the renderer's formatDate accepts (ISO, DD.MM.YYYY,
// DD/MM/YYYY, DD-MM-YYYY, unix seconds) and prints DD.MM.YYYY / YYYY-MM-DD.
function formatDateValue(v, layout) {
  const s = String(v == null ? '' : v).trim();
  if (!s) return s;
  let y; let m; let d;
  let hit = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(s);
  if (hit) [, y, m, d] = hit;
  else if ((hit = /^(\d{2})[./-](\d{2})[./-](\d{4})$/.exec(s))) [, d, m, y] = hit;
  else if (/^\d{10,}$/.test(s)) {
    const dt = new Date(Number(s) * 1000);
    y = String(dt.getUTCFullYear()); m = String(dt.getUTCMonth() + 1).padStart(2, '0'); d = String(dt.getUTCDate()).padStart(2, '0');
  } else return s;
  return layout === 'dmy' ? `${d}.${m}.${y}` : `${y}-${m}-${d}`;
}

// Go's strings.ToUpper / ToLower map one rune to one rune ("ß" stays
// "ß"); JS uses full case mapping ("ß" → "SS"). Keep single-rune results
// only, so the preview prints what the renderer prints.
function simpleCase(v, dir) {
  let out = '';
  for (const ch of v) {
    const mapped = [...(dir === 'upper' ? ch.toUpperCase() : ch.toLowerCase())];
    out += mapped.length === 1 ? mapped[0] : (dir === 'lower' ? mapped[0] : ch);
  }
  return out;
}

function previewFieldValue(field, raw, defaults) {
  const f = field || {};
  const dflt = defaults || {};
  let v = raw == null ? '' : String(raw);
  switch (f.format) {
    case 'uppercase': v = simpleCase(v, 'upper'); break;
    case 'lowercase': v = simpleCase(v, 'lower'); break;
    case 'currency': {
      const n = parseNumberLoose(v);
      if (n != null) v = applyNumberSeparators(toFixedHalfEven(n, 2), 'us-grouped');
      break;
    }
    case 'date-dmy': v = formatDateValue(v, 'dmy'); break;
    case 'date-ymd': v = formatDateValue(v, 'ymd'); break;
    default: break;
  }
  if (v === '') return v;
  if (f.type === 'number') {
    const n = parseNumberLoose(v);
    if (n == null) return v;
    let places = f.decimalPlaces == null ? 2 : Math.min(10, Math.max(0, Number(f.decimalPlaces) || 0));
    if (f.showDecimals === false) places = 0;
    return applyNumberSeparators(toFixedHalfEven(n, places), dflt.numberFormat);
  }
  if (f.type === 'currency') {
    const n = parseNumberLoose(v);
    if (n == null) return v;
    const places = f.decimalPlaces == null ? 2 : Math.min(4, Math.max(0, Number(f.decimalPlaces) || 0));
    const code = (String(f.currencyCode || '').trim() || String(dflt.defaultCurrency || '').trim() || 'USD').toUpperCase();
    const fmt = String(dflt.currencyFormat || '');
    const amount = applyNumberSeparators(toFixedHalfEven(n, places), fmt.endsWith('-eu') ? 'eu-grouped' : 'us-grouped');
    const symbol = currencySymbol(code);
    return fmt.startsWith('symbol-after') ? amount + symbol : symbol + amount;
  }
  return v;
}

module.exports = {
  numberInputChars,
  normalizeNumberInput,
  normalizeJsonKey,
  acroNameToPath,
  setPathDeep,
  buildInlineSchemaFromFields,
  parseCsvRows,
  flattenDotKeys,
  csvEscapeCell,
  csvToJobItems,
  sampleFromSchema,
  schemaForTemplate,
  jobSampleText,
  fieldKeyFromName,
  nextFieldName,
  uniqueDataKey,
  humanizeKey,
  schemaLeafType,
  fillFormFromFields,
  initialFillValues,
  buildFillData,
  previewFieldValue,
  JSON_SAMPLE,
  CSV_SAMPLE,
};
